import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageProperties } from '../enums/storage-properties.enum';
import { TokenI } from '../models/token.model';
import { JsBridgeService } from './js-bridge.service';

@Injectable({ providedIn: 'root' })
export class LocalStorageService {

  private deviceId: string | null = null;

  private readonly isReviewMode$ = new BehaviorSubject<boolean>(false);

  get isReviewMode(): boolean {
    return this.isReviewMode$.value;
  }

  set isReviewMode(value: boolean) {
    this.isReviewMode$.next(value);
  }

  readonly reviewMode$: Observable<boolean> = this.isReviewMode$.asObservable();

  constructor(private bridge: JsBridgeService) {}

  // --- Login ---

  saveLogInProperties(accessToken: string, refreshToken: string, expiration: string): void {
    localStorage.setItem(StorageProperties.ACCESS_TOKEN, accessToken);
    localStorage.setItem(StorageProperties.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(StorageProperties.EXPIRATION, expiration);

    this.bridge.saveSharedPreferences(StorageProperties.ACCESS_TOKEN, accessToken);
    this.bridge.saveSharedPreferences(StorageProperties.REFRESH_TOKEN, refreshToken);
    this.bridge.saveSharedPreferences(StorageProperties.EXPIRATION, expiration);
  }

  savePreAuth(accessToken: string): void {
    localStorage.setItem(StorageProperties.ACCESS_TOKEN, accessToken);
    this.bridge.saveSharedPreferences(StorageProperties.ACCESS_TOKEN, accessToken);

    localStorage.removeItem(StorageProperties.REFRESH_TOKEN);
    localStorage.removeItem(StorageProperties.EXPIRATION);
    localStorage.removeItem(StorageProperties.USER_ID);

    this.bridge.removeSharedValues([
      StorageProperties.REFRESH_TOKEN,
      StorageProperties.EXPIRATION,
      StorageProperties.USER_ID,
    ]);
  }

  cleanLoginProperties(): void {
    const accessToken = this.getAccessToken();
    const userId = this.getUserId();

    localStorage.removeItem(StorageProperties.ACCESS_TOKEN);
    localStorage.removeItem(StorageProperties.REFRESH_TOKEN);
    localStorage.removeItem(StorageProperties.EXPIRATION);
    localStorage.removeItem(StorageProperties.USER_ID);

    this.bridge.removeSharedValues([
      StorageProperties.ACCESS_TOKEN,
      StorageProperties.REFRESH_TOKEN,
      StorageProperties.EXPIRATION,
      StorageProperties.USER_ID,
    ]);

    if (accessToken) {
      this.bridge.logout({ userId: userId || '', accessToken });
    }
  }

  // --- Storage ---

  saveUserId(user: { id: string }): void {
    localStorage.setItem(StorageProperties.USER_ID, user.id);
    this.bridge.saveSharedPreferences(StorageProperties.USER_ID, user.id);
  }

  getUserId(): string | null {
    return localStorage.getItem(StorageProperties.USER_ID);
  }

  saveGroup(group: string): void {
    localStorage.setItem(StorageProperties.GROUP, group);
    this.bridge.saveSharedPreferences(StorageProperties.GROUP, group);
  }

  getGroup(): string | null {
    return localStorage.getItem(StorageProperties.GROUP);
  }

  cleanGroup(): void {
    localStorage.removeItem(StorageProperties.GROUP);
    this.bridge.removeSharedValues([StorageProperties.GROUP]);
  }

  // --- Token (sincroni) ---

  getAccessToken(): string | null {
    return localStorage.getItem(StorageProperties.ACCESS_TOKEN);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(StorageProperties.REFRESH_TOKEN);
  }

  getExpiration(): string | null {
    return localStorage.getItem(StorageProperties.EXPIRATION);
  }

  // --- JWT decode ---

  decodeToken(): TokenI | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }
    try {
      return jwtDecode<TokenI>(token);
    } catch {
      return null;
    }
  }

  getEmail(): string | null {
    const decoded = this.decodeToken();
    return decoded?.sub ?? null;
  }

  // --- Piattaforma ---

  getPlatform(): string | null {
    return localStorage.getItem(StorageProperties.PLATFORM);
  }

  isMobilePlatform(): boolean {
    const platform = this.getPlatform()?.toLowerCase();
    return platform === 'android' || platform === 'ios';
  }

  getIsReviewMode(): boolean {
    return this.isReviewMode;
  }

  setDeviceId(id: string): void {
    this.deviceId = id;
  }

  getDeviceId(): string | null {
    return this.deviceId;
  }
}
